import React, {useEffect, useState} from "react";
import AuthorizationContext from "./index";
import axiosConfig from "../../Config/axios";
import {getSecureAuthenticationRoute} from "../../Route/RouteIndex";
import {toast} from "react-toastify";
import type {AuthUser, ErrorHandlerFunction, AuthorizationProviderProps, AxiosError, SetError, AuthorizationContextType} from "./AuthorizationTypes";

const AuthorizationProvider: React.FC<AuthorizationProviderProps> = (props) => {
    const [authorizedUser, setAuthorizedUser] = useState<AuthUser>({});
    const authenticated: boolean = !!localStorage.getItem("authenticated_user") && !!localStorage.getItem("authenticated_token");

    const logOut = (): void => {
        localStorage.removeItem("authenticated_user");
        localStorage.removeItem("authenticated_token");
        document.location.href = `${process.env.PUBLIC_URL}/login`;
    };

    useEffect(() => {
        authenticated && initiateAuthUser();
    }, [authenticated]);

    const initiateAuthUser = (): void => {
        axiosConfig
            .get(`${getSecureAuthenticationRoute('initial-session')}`)
            .then((response: { data: { data: AuthUser; }; }) => {
                if (response?.data?.data) {
                    setAuthorizedUser(response?.data?.data);
                }
            })
            .catch((exception: AxiosError | any) => {
                errorHandler(exception);
            });
    };

    const errorHandler: ErrorHandlerFunction = (exception: AxiosError | string | any, setError: SetError | null = null): void => {
        if (typeof exception === 'object' && exception?.response?.status) {
            switch (exception.response.status) {
                case 422:
                    if (setError) {
                        Object.keys(exception.response.data?.errors || {}).forEach((name: string) => {
                            setError(name, {message: exception.response.data?.errors[name] || []});
                        });
                    } else {
                        toast.error(`${Object.keys(exception.response.data?.errors || {}).length} Validation Error(s)`, {
                            position: toast.POSITION.TOP_RIGHT,
                            autoClose: 2000,
                        });
                    }
                    break;
                case 401:
                    logOut();
                    break;
                case 403:
                case 500:
                case 400:
                default:
                    toast.error(exception?.response.data?.status?.msg || `Oops! Something went wrong! Please try again`, {
                        position: toast.POSITION.TOP_RIGHT,
                        autoClose: 2000,
                    });
            }
        } else {
            toast.error(String(exception), {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000,
            });
        }
    };

    const hasPermission = (permission: string): boolean => {
        const isSuperAdmin = authorizedUser?.system_roles?.some((role: string) => role?.toLowerCase() === 'super-admin') || false;
        if (isSuperAdmin) return true;
        return authorizedUser?.permissions?.includes(permission) || false;
    };

    const hasAnyPermission = (permissions: string[]): boolean => {
        const isSuperAdmin = authorizedUser?.system_roles?.some((role: string) => role?.toLowerCase() === 'super-admin') || false;
        if (isSuperAdmin) return true;
        if (!authorizedUser?.permissions || !permissions.length) return false;
        return permissions.some(permission => authorizedUser.permissions!.includes(permission));
    };

    const hasAllPermissions = (permissions: string[]): boolean => {
        const isSuperAdmin = authorizedUser?.system_roles?.some((role: string) => role?.toLowerCase() === 'super-admin') || false;
        if (isSuperAdmin) return true;
        if (!authorizedUser?.permissions || !permissions.length) return false;
        return permissions.every(permission => authorizedUser.permissions!.includes(permission));
    };

    const contextValue: AuthorizationContextType = {
        ...props,
        logOut,
        errorHandler,
        authorizedUser,
        setAuthorizedUser,
        authenticated,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
    };

    return (
        <AuthorizationContext.Provider value={contextValue}>
            {props.children}
        </AuthorizationContext.Provider>
    );
};

export default AuthorizationProvider;
